import { Inject, Injectable } from '@angular/core';
import * as _ from 'lodash';
import { ImCachedObject } from '../im-models/im-cached-object.service';
import { ObjectHash } from '../object-hash/object-hash.service';
import { UtilitiesService } from '../utilities/utilities.service';
import { LocalStorageService } from '../web-storage/local-storage/local-storage.service';

@Injectable()
export class ObjectCache {
  constructor(
    private objectHash: ObjectHash,
    private utilitiesService: UtilitiesService,
    private imCachedObject: ImCachedObject,
    private localStorageService: LocalStorageService,
    @Inject('CachedObject') private cachedObject,
    @Inject('$q') private $q,
    @Inject('Rollbar') private rollbar,
  ) {
    // Ensure user has current cache version
    const EXPECTED_OBJECT_CACHE_VERSION = 1;
    const CURRENT_USER_VERSION = this.localStorageService.getItem('ObjectCacheVersion');
    if (EXPECTED_OBJECT_CACHE_VERSION !== CURRENT_USER_VERSION) {
      this.localStorageService.removeItem('ObjectCache');
      this.localStorageService.setItem('ObjectCacheVersion', EXPECTED_OBJECT_CACHE_VERSION);
    }
  }
  // Max number of items to be cached in localStorage
  // Note: Chrome has a limit of 5MB stored in localStorage for the entire origin, so don't make this too large
  _MAX_LOCAL_CACHE_SIZE = 100;
  _version = null;

  // Returns a promise that is resolved with the object cached at `key`. If no object is found, resolves the
  // promise with undefined.
  getObject(key) {
    const deferred = this.$q.defer();
    const localValue = this._getFromLocalStorage(key);
    if (_.isUndefined(localValue)) {
      this.cachedObject.get(
        { id: key },
        cachedObject => {
          deferred.resolve(JSON.parse(cachedObject.json));
        },
        err => {
          // dont reject
          this.rollbar.warning(`Local cache_object with _id ${key} was never persisted to DB`, err);
          deferred.resolve(null);
        },
      );
    } else {
      deferred.resolve(localValue);
    }
    return deferred.promise;
  }

  // Caches an object and returns the cache key
  cacheObject(object) {
    const key = this.objectHash.createHash(object);
    const cacheObject: any = this._addToLocalStorage(key, object);
    // only hit api if newly added:
    if (!cacheObject.persisted) {
      const backendCachedObject = this.imCachedObject.createFromObject(object);
      backendCachedObject.$create(
        null,
        () => {
          // capture returned object from fn to check status:
          this._recordObjectPersisted(cacheObject.key);
        },
        err => {
          this.rollbar.warning(`Error saving cached object`, err);
        },
      );
    }
    return key;
  }

  _recordObjectPersisted(key) {
    const cache = this._getObjectCacheFromLocalStorage() || [];
    const cacheObject: any = _.find(cache, { key });
    if (cacheObject) {
      cacheObject.persisted = true;
      this.localStorageService.setItem('ObjectCache', cache);
    }
    return;
  }

  _addToLocalStorage(key, object) {
    const cache = this._getObjectCacheFromLocalStorage() || [];
    // if maxed out, remove the oldest cached obj:
    if (cache.length >= this._MAX_LOCAL_CACHE_SIZE) {
      cache.shift();
    }
    const existingObject = _.find(cache, { key });
    if (!existingObject) {
      const newObject = { key, value: this.utilitiesService.toPOJO(object), persisted: false };
      cache.push(newObject);
      this.localStorageService.setItem('ObjectCache', cache);
      return newObject;
    } else {
      return existingObject;
    }
  }

  _getFromLocalStorage(key) {
    const cache = this._getObjectCacheFromLocalStorage();
    if (cache) {
      const item: any = _.find(cache, { key });
      return item ? item.value : undefined;
    } else {
      return undefined;
    }
  }

  _getObjectCacheFromLocalStorage() {
    let cache = this.localStorageService.getItem('ObjectCache');
    // we expect the cache returned from local storage to be stringified
    while (typeof cache === 'string') {
      try {
        cache = JSON.parse(cache);
      } catch (ex) {
        cache = undefined;
        this.localStorageService.removeItem('ObjectCache');
      }
    }
    return cache;
  }
}
